import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { companyApi } from '@/api/company.api';
import type { CompanyDto } from '@/types/company.types';
import type { RootState } from '@/state/store';

/**
 * The signed-in company's full record — for document letterheads.
 *
 * The session carries a code and a name, which is enough for a title bar and not enough for a
 * printed voucher: a document that leaves the building needs the legal name, the address and the
 * registration number on it. Those live only on the company record, so this fetches it.
 *
 * Cached for the life of the module. A company's letterhead does not change between two cheques,
 * and re-fetching it for every preview would put a request behind every Print button.
 */
const cache = new Map<string, CompanyDto>();

export function useCompanyProfile() {
  const session   = useSelector((s: RootState) => s.session.session);
  const companyId = session?.companyId ?? '';

  const [company, setCompany] = useState<CompanyDto | null>(
    () => (companyId ? cache.get(companyId) ?? null : null));

  useEffect(() => {
    if (!companyId) { setCompany(null); return; }

    const hit = cache.get(companyId);
    if (hit) { setCompany(hit); return; }

    let current = true;
    companyApi.getById(companyId)
      .then(r => {
        const dto = r.data.data;
        if (!dto) return;
        cache.set(companyId, dto);
        if (current) setCompany(dto);
      })
      // A voucher must still print without it. The fallback below uses the session's own name,
      // which is always present — better a letterhead with just the company name than a failed
      // preview.
      .catch(() => { /* fall through to the session name */ });

    return () => { current = false; };
  }, [companyId]);

  /** Address lines, in the order they are written on an envelope, blanks dropped. */
  const addressLines = [
    company?.addressLine1,
    company?.addressLine2,
    [company?.city, company?.countryCode].filter(Boolean).join(', ') || null,
  ].filter((l): l is string => Boolean(l && l.trim()));

  return {
    company,
    /** The name to print. Legal name where there is one — that is the name on the mandate. */
    displayName: company?.legalName?.trim() || company?.name || session?.companyName || '',
    addressLines,
    registrationNumber: company?.registrationNumber ?? null,
    taxNumber:          company?.taxNumber ?? null,
    phone:              company?.phone ?? null,
    email:              company?.email ?? null,
  };
}
